/** Human-readable formatting helpers for the fleet UI. */

import type { DeviceStatus, DeviceMode } from "./api";

const UNITS = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];

/** Format a byte count as a human-readable binary size, e.g. 1.4 GiB. */
export function humanBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  if (bytes < 1) return "0 B";
  const i = Math.min(UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const v = bytes / Math.pow(1024, i);
  return `${v.toFixed(i === 0 ? 0 : 1)} ${UNITS[i]}`;
}

/** Format Hz with a sensible SI-ish unit (kHz/MHz/GHz). */
export function humanHz(hz: number | null | undefined): string {
  if (hz == null || Number.isNaN(hz)) return "—";
  if (hz >= 1e9) return `${(hz / 1e9).toFixed(3)} GHz`;
  if (hz >= 1e6) return `${(hz / 1e6).toFixed(3)} MHz`;
  if (hz >= 1e3) return `${(hz / 1e3).toFixed(1)} kHz`;
  return `${hz} Hz`;
}

/** Format sample rate (sps) compactly, e.g. 2.4 Msps. */
export function humanRate(sps: number | null | undefined): string {
  if (sps == null || Number.isNaN(sps)) return "—";
  if (sps >= 1e6) return `${(sps / 1e6).toFixed(2)} Msps`;
  if (sps >= 1e3) return `${(sps / 1e3).toFixed(1)} ksps`;
  return `${sps} sps`;
}

/** Relative "time ago" for last_seen; absolute tooltip handled by caller. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/** A device is considered offline if no heartbeat in > 90s (3 missed @30s). */
export function isOffline(lastSeen: string | null | undefined): boolean {
  if (!lastSeen) return true;
  const then = Date.parse(lastSeen);
  if (Number.isNaN(then)) return true;
  return Date.now() - then > 90_000;
}

export type IcStatus = "neutral" | "success" | "warning" | "danger";

// --- health badge thresholds (SPEC §10) ------------------------------------
// CPU: warn ≥75%, danger ≥90%.
export function cpuStatus(pct: number | null | undefined): IcStatus {
  if (pct == null || Number.isNaN(pct)) return "neutral";
  if (pct >= 90) return "danger";
  if (pct >= 75) return "warning";
  return "success";
}
// Temperature: Pi 4 soft-throttles at 80 °C, hard-limits at 85 °C — so warn at
// 70 °C (early heads-up), danger at 80 °C (throttling has begun).
export function tempStatus(c: number | null | undefined): IcStatus {
  if (c == null || Number.isNaN(c)) return "neutral";
  if (c >= 80) return "danger";
  if (c >= 70) return "warning";
  return "success";
}
// Disk free: warn below 2 GB, danger below 1 GB.
export function diskStatus(bytes: number | null | undefined): IcStatus {
  if (bytes == null || Number.isNaN(bytes)) return "neutral";
  if (bytes < 1024 ** 3) return "danger";
  if (bytes < 2 * 1024 ** 3) return "warning";
  return "success";
}

/** Friendly pill text for a health status (CPU/temp/disk badges) — plain words
 * instead of the raw "success"/"danger" status codes. */
export function healthLabel(s: IcStatus): string {
  switch (s) {
    case "success":
      return "ok";
    case "warning":
      return "warning";
    case "danger":
      return "priority warning";
    default:
      return "unknown";
  }
}

/** Map device status (+ liveness) to an ic-status-tag status colour. */
export function statusTagStatus(
  status: DeviceStatus,
  lastSeen: string | null | undefined,
): IcStatus {
  if (status === "disabled") return "danger";
  if (status === "pending") return "warning";
  // active:
  return isOffline(lastSeen) ? "neutral" : "success";
}

/** Human label for the status column, accounting for offline. */
export function statusLabel(
  status: DeviceStatus,
  lastSeen: string | null | undefined,
): string {
  if (status === "active" && isOffline(lastSeen)) return "Offline";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Map device mode to an ic-status-tag status colour. */
export function modeTagStatus(mode: DeviceMode | null | undefined): IcStatus {
  switch (mode) {
    case "live":
      return "success";
    case "capture":
    case "decode":
      return "warning";
    default:
      return "neutral";
  }
}
